import {
    Box,
    Button, capitalize,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import React, {
    ChangeEvent,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";
import {useNavigate} from "react-router";
import {GridColDef} from "@mui/x-data-grid";
import {
    DataLoader,
    DataRequest,
    DataRequestState,
    useDataRequest,
} from "../data";
import {useT} from "../../i18n";
import SearchField from "../SearchField";
import {useGetApi} from "../../client";
import {CreateAnhang, EingangsrechnungInfo} from "../../api";
import {useCreateRechnungsAnhang} from "../../client/hooks";
import {useMutation, useQueryClient} from "react-query";
import {Field, Formik} from "formik";
import PrueferSelect from "../benutzer/PrueferSelect";
import Guard from "../Guard";
import DataTable from "../data/DataTable";
import RechnungStatusChip from "./RechnungStatusChip";
import {formatAsMoney} from "../../formatterHelpers";
import useCustomFilters from "../data/useCustomFilters";

export type EingangsrechnungenFilter = {
    search?: string;
    status?: string;
    pruefer?: string;
    filtered?: string;
};

export type Props = Omit<DataRequest<EingangsrechnungenFilter>, "filter"> &
    Partial<Pick<DataRequest<EingangsrechnungenFilter>, "filter">>;

const tableName: string = "eingangsrechnungen"

export default function EingangsrechnungenDataTable({...input}: Props) {
    const request = useDataRequest<EingangsrechnungenFilter>({
        filter: {},
        ...input,
    });
    const [filteredState, setFilteredState] = useState("");

    return (
        <Stack spacing={2}>
            <FilterComp
                request={request}
                filteredStateFilter={filteredState}
            />
            <EingangsrechnungenResults
                request={request}
                setFilteredState={setFilteredState}
            />
        </Stack>
    );
}

function FilterComp({
                        request,
                        filteredStateFilter
                    }: {
    request: DataRequestState<EingangsrechnungenFilter>;
    filteredStateFilter: string;
}) {
    const {setFilter} = request;
    const {currentCustomFilter, setCustomField} = useCustomFilters<EingangsrechnungenFilter>({
        tableName: tableName,
        defaultFilter: {
            search: "",
            status: "_all",
            pruefer: undefined,
        }
    });
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const {t: translation} = useT("eingangsrechnung");
    const {mutateAsync} = useSaveRechnungsanhang();

    const rechnungStatus = {
        _all: "Alle",
        IN_ERKENNUNG: "In Erkennung",
        FEHLER_GOOGLE: "Fehler Google",
        OFFEN: "Offen",
        IN_PRUEFUNG: "In Prüfung",
        FREIGEGEBEN: "Freigegeben",
        VERARBEITUNG_DATEV: "Verarbeitung Datev",
        FEHLER_BEI_UEBERTRAG_DATEV: "Fehler Datev",
        IN_DATEV: "In Datev",
        BEZAHLT: "Bezahlt",
        STORNIERT: "Storniert",
        ABGELEHNT: "Abgelehnt",
    };

    const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files) {
            const fileArray = Array.from(files);
            for (const file of fileArray) {
                await mutateAsync({
                    type: file.type,
                    filename: file.name,
                    file: file,
                    name: file.name,
                });
            }
        }
    };

    useEffect(() => {
        setFilter({
            search: currentCustomFilter.search,
            status: currentCustomFilter.status,
            pruefer: currentCustomFilter.pruefer,
            filtered: filteredStateFilter || undefined,
        });
    }, [currentCustomFilter, filteredStateFilter, setFilter]);

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                position: "relative",
                width: "100%",
            }}
        >
            {isMobile ? (
                <Stack direction="row">
                    <SearchField
                        value={currentCustomFilter.search}
                        onChange={(val) => setCustomField("search", val)}
                    />
                    <FormControl size="small" sx={{width: "150px", ml: 1}}>
                        <InputLabel>Belegstatus</InputLabel>
                        <Select
                            label="Belegstatus"
                            size="small"
                            value={currentCustomFilter.status ?? "_all"}
                            onChange={(val) => setCustomField("status", val.target.value)}
                        >
                            {Object.entries(rechnungStatus).map(([key, value]) => (
                                <MenuItem key={key} value={key}>
                                    {value}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Formik initialValues={{pruefer: currentCustomFilter.pruefer}} enableReinitialize onSubmit={() => {
                    }}>
                        <Field
                            name="pruefer"
                            component={PrueferSelect}
                            label={"Prüfer"}
                            required={false}
                            setPruefer={(val: any) => setCustomField("pruefer", val)}
                        />
                    </Formik>
                    <Button variant="contained" component="label" color={"secondary"}>
                        {capitalize(translation("upload"))}
                        <input
                            type="file"
                            hidden
                            accept="application/pdf"
                            onChange={handleFileChange}
                            multiple={true}
                        />
                    </Button>
                </Stack>
            ) : (
                <>
                    <SearchField
                        value={currentCustomFilter.search}
                        onChange={(val) => setCustomField("search", val)}
                    />
                    <FormControl size="small" sx={{width: "150px", ml: 1}}>
                        <InputLabel>Belegstatus</InputLabel>
                        <Select
                            label="Belegstatus"
                            size="small"
                            value={currentCustomFilter.status ?? "_all"}
                            onChange={(val) => setCustomField("status", val.target.value)}
                        >
                            {Object.entries(rechnungStatus).map(([key, value]) => (
                                <MenuItem key={key} value={key}>
                                    {value}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Formik initialValues={{pruefer: currentCustomFilter.pruefer}} onSubmit={() => {
                    }}>
                        <Field
                            name="pruefer"
                            component={PrueferSelect}
                            label={"Prüfer"}
                            required={false}
                            setPruefer={(val: any) => setCustomField("pruefer", val)}
                        />
                    </Formik>
                    <Guard permission={"eingangsrechnung:create"}>
                        <Button
                            variant="contained"
                            component="label"
                            sx={{position: "absolute", top: 0, right: 0}}
                            color={"secondary"}
                        >
                            {capitalize(translation("upload"))}
                            <input
                                type="file"
                                hidden
                                accept="application/pdf"
                                onChange={handleFileChange}
                                multiple={true}
                            />
                        </Button>
                    </Guard>
                </>
            )}
        </Box>
    );
}

function useSaveRechnungsanhang() {
    const createRechnungsAnhang = useCreateRechnungsAnhang();
    const queryClient = useQueryClient();

    return useMutation(
        (input: CreateAnhang) => {
            return createRechnungsAnhang(input);
        },
        {
            onSuccess: async () => {
                await queryClient.invalidateQueries([tableName]);
            },
        }
    );
}

function EingangsrechnungenResults({
                                       request,
                                       setFilteredState,
                                   }: {
    request: DataRequestState<EingangsrechnungenFilter>;
    setFilteredState: (value: string) => void;
}) {
    return <Results request={request} setFilteredState={setFilteredState}/>;
}

function Results({
                     request,
                     setFilteredState,
                 }: {
    request: DataRequestState<EingangsrechnungenFilter>;
    setFilteredState: (value: string) => void;
}) {
    const navigate = useNavigate();
    const getApi = useGetApi();
    const loadEingangsrechnungen: DataLoader<
        EingangsrechnungenFilter,
        EingangsrechnungInfo
    > = useCallback(
        async (params) =>
            (await getApi()).rechnungen.sucheRechnungen({...params}),
        [getApi]
    );
    return (
        <DataTable
            columns={useColumns()}
            request={request}
            queryKey={[tableName]}
            loadData={loadEingangsrechnungen}
            onRowClick={(row, event) => {
                if (event.ctrlKey || event.metaKey) {
                    window.open(`/eingangsrechnungen/${row.id}`, "_blank");
                } else {
                    const currentSearch = window.location.search;
                    const params = new URLSearchParams();
                    if (currentSearch) {
                        params.set("returnSearch", currentSearch);
                    }
                    const query = params.toString();
                    navigate(`/eingangsrechnungen/${row.id}` + (query ? "?" + query : ""));
                }
            }}
            setFiltered={setFilteredState}
        />
    );
}

function useColumns(): Array<GridColDef<EingangsrechnungInfo>> {
    const {t: translation} = useT("eingangsrechnung");

    return useMemo(
        () => [
            {
                field: "belegTyp",
                headerName: capitalize(translation("document-type")),
                flex: 1,
            },
            {
                field: "rechnungsDatum",
                headerName: capitalize(translation("date")),
                type: "date",
                valueGetter: (value) => {
                    return new Date(value);
                },
                renderCell: ({value}) =>
                    value != "Invalid Date"
                        ? new Date(value).toLocaleDateString(undefined, {
                            month: "2-digit",
                            day: "2-digit",
                            year: "numeric",
                        })
                        : capitalize(translation("no-date")),
                width: 100,
            },
            {
                field: "lieferantName",
                headerName: capitalize(translation("supplier")),
                flex: 1,
            },
            {
                field: "rechnungsNummer",
                headerName: capitalize(translation("invoice-number-short")),
                flex: 1,
            },
            {
                field: "baustellenNamen",
                headerName: capitalize(translation("construction-site")),
                flex: 1,
            },
            {
                field: "baustellenNummern",
                headerName: capitalize(translation("construction-site-number")),
                flex: 1,
            },
            {
                field: "prueferNamen",
                headerName: capitalize(translation("proufer")),
                flex: 1,
            },
            {
                field: "belegStatus",
                headerName: capitalize(translation("document-status")),
                renderCell: (params) => {
                    const rechnung: EingangsrechnungInfo = params.row;
                    return <RechnungStatusChip eingangsrechnung={rechnung}/>;
                },
                flex: 1,
            },
            {
                field: "nettoBetrag",
                headerName: capitalize(translation("net-amount")),
                flex: 1,
                align: "right",
                headerAlign: "right",
                type: "number",
                renderCell: ({value}) => {
                    return formatAsMoney(value);
                },
            },
            {
                field: "bruttoBetrag",
                headerName: capitalize(translation("gross-amount")),
                flex: 1,
                align: "right",
                headerAlign: "right",
                type: "number",
                renderCell: ({value}) => {
                    return formatAsMoney(value);
                },
            },
        ],
        [translation]
    );
}
